// src/components/avisEnseignants/AvisEnseignantsOnglet.jsx
// ============================================================================
// Vie scolaire — onglet « Avis des enseignants »
// ============================================================================
//
// Les avis que les enseignants donnent sur leurs élèves depuis leur espace :
// une liste fermée de libellés (« Travail non fait », « En progrès »…) et un
// commentaire facultatif. La vie scolaire les lit, les marque lus, et décide
// si un fait mérite un incident.
//
// Trois règles de Daniel (6 octobre 2026), que cet écran rappelle :
//   - un avis n'est JAMAIS montré à la famille ni à l'élève (table à part, que
//     l'espace famille ne lit pas) ;
//   - il n'entre pas dans le score de l'outil décisionnel ;
//   - pour qu'un fait remonte aux parents, la vie scolaire crée elle-même un
//     incident, qu'elle rédige : le texte de l'avis n'est jamais recopié
//     (DialogIncidentAvis).
//
// Lire demande ELEVES.AVIS_ENSEIGNANTS.VIEW (la page n'affiche pas l'onglet
// sans elle) ; marquer lu et créer un incident, ELEVES.AVIS_ENSEIGNANTS.TRAITER,
// que le serveur annonce dans `droits.traiter`. Sans elle, l'écran se lit sans
// proposer d'action.
// ============================================================================

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, ChevronLeft, ChevronRight, Inbox, Loader2, Search, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import ChampSelect from '../ui/ChampSelect';
import EtatVide from '../ui/EtatVide';
import { Toast } from '../ui/Toast';
import LongToast, { shouldUseLongToast } from '../ui/LongToast';
import ToastPortal from '../ToastPortal';
import { useFeatures } from '../../contexts/FeaturesContext';
import { triggerVieScolaireUpdate } from '../../hooks/useVieScolaire';
import { avisEnseignants } from './apiAvisEnseignants';
import DialogIncidentAvis from './DialogIncidentAvis';
import { LigneAvis, intituleAvis } from './presentationAvis';
// Les pastilles de filtre viennent de l'ecran jumeau : elles y sont deja a
// l'arrondi d'un bouton (consigne du 07/10/2026). Cet onglet en avait une
// copie en dur, a un mot pres.
import { classesFiltre } from '../validationNotes/presentationNotes';

const STATUTS = [
  { cle: 'nouveau', libelle: 'Nouveaux' },
  { cle: 'lu', libelle: 'Lus' },
  { cle: 'incident', libelle: 'Devenus incidents' },
  { cle: 'tous', libelle: 'Tous' }
];

// Radix refuse une valeur vide dans une liste : « toutes » tient lieu de
// « pas de filtre » et ne part pas au serveur.
const TOUTES = 'toutes';
const TONALITES = [
  { valeur: TOUTES, libelle: 'Toutes les tonalités' },
  { valeur: 'vigilance', libelle: 'À surveiller' },
  { valeur: 'mixte', libelle: 'Mixtes' },
  { valeur: 'positif', libelle: 'Positifs' }
];

const VIDES = {
  nouveau: ['Aucun avis à lire', 'Les avis donnés par les enseignants depuis leur espace apparaîtront ici.'],
  lu: ['Aucun avis lu', 'Les avis marqués lus par la vie scolaire apparaîtront ici.'],
  incident: ['Aucun avis devenu incident', 'Les avis dont la vie scolaire a fait un incident apparaîtront ici.'],
  tous: ['Aucun avis', "Aucun enseignant n'a encore donné d'avis cette année."]
};

// Pagination du serveur (50 avis par page). Le composant `ui/Pagination`
// accorde son libellé en ajoutant un « s » : il écrirait « 120 aviss ».
const PaginationAvis = ({ page, nombrePages, total, parPage, onChangerPage, desactive }) => {
  const premier = (page - 1) * parPage + 1;
  const dernier = Math.min(page * parPage, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 py-3">
      <p className="text-[11px] text-gray-500">
        {nombrePages > 1 ? `${premier} - ${dernier} sur ${total} avis` : `${total} avis`}
      </p>
      {nombrePages > 1 && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1" disabled={desactive || page <= 1} onClick={() => onChangerPage(page - 1)}>
            <ChevronLeft className="h-3.5 w-3.5" />
            Précédent
          </Button>
          <span className="text-[11px] font-semibold tabular-nums text-gray-600">{page} / {nombrePages}</span>
          <Button variant="outline" size="sm" className="gap-1" disabled={desactive || page >= nombrePages} onClick={() => onChangerPage(page + 1)}>
            Suivant
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

const AvisEnseignantsOnglet = ({ classes = [], onCompteurs }) => {
  const { estActive } = useFeatures();
  const [statut, setStatut] = useState('nouveau');
  const [tonalite, setTonalite] = useState(TOUTES);
  const [classeId, setClasseId] = useState(TOUTES);
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);
  const [incidentPour, setIncidentPour] = useState(null);
  const [toast, setToast] = useState(null);
  const idTonalite = useId();
  const idClasse = useId();

  // Chaque lecture porte un numéro : seule la réponse de la DERNIÈRE s'affiche.
  // En changeant vite de filtre, une réponse lente remettrait sinon à l'écran
  // la liste d'un filtre qu'on a déjà quitté.
  const demande = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++demande.current;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await avisEnseignants.liste({
        statut,
        tonalite: tonalite === TOUTES ? '' : tonalite,
        classe_id: classeId === TOUTES ? '' : classeId,
        q: recherche.trim(),
        page
      });
      if (numero !== demande.current) return;
      setDonnees(reponse.data);
      if (reponse.data?.compteurs) onCompteurs?.(reponse.data.compteurs);
    } catch (e) {
      if (numero === demande.current) setErreur(e.message);
    } finally {
      if (numero === demande.current) setChargement(false);
    }
  }, [statut, tonalite, classeId, recherche, page, onCompteurs]);

  // La recherche attend qu'on ait fini de taper ; les autres filtres partent aussitôt.
  useEffect(() => {
    const minuterie = setTimeout(charger, recherche ? 300 : 0);
    return () => clearTimeout(minuterie);
  }, [charger, recherche]);

  // Le dernier avis d'une dernière page vient d'être marqué lu (il quitte la
  // liste des « nouveaux ») : la page est vide alors qu'il en reste avant elle.
  // On recule à la dernière page qui existe encore, plutôt que d'afficher
  // « Aucun avis à lire » au-dessus de « 51–50 sur 50 ».
  useEffect(() => {
    if (!donnees || page <= 1) return;
    const restants = Number(donnees.total || 0);
    const vide = !Array.isArray(donnees.avis) || donnees.avis.length === 0;
    if (vide && restants > 0) {
      setPage(Math.max(1, Math.ceil(restants / Number(donnees.par_page || 50))));
    }
  }, [donnees, page]);

  // Un autre filtre : retour à la première page, la page 3 d'une autre liste
  // n'a pas de sens.
  const filtrer = (changer) => (valeur) => {
    changer(valeur);
    setPage(1);
  };

  const effacerFiltres = () => {
    setTonalite(TOUTES);
    setClasseId(TOUTES);
    setRecherche('');
    setPage(1);
  };

  const montrerToast = (message, type = 'success') => setToast({ message, type });

  // Un refus dit souvent que l'écran est en retard sur la base : un avis lu ou
  // devenu incident entre-temps par un collègue. On relit pour cesser de
  // proposer ce que le serveur vient de refuser. Une panne réseau (sans
  // statut) ne change rien à l'état : on ne relit pas.
  const refus = async (e) => {
    montrerToast(e.message, 'error');
    if (e?.statut) await charger();
  };

  // Verrou immédiat : `enCours` ne désactive les boutons qu'au rendu suivant ;
  // un second clic arrivé avant lui enverrait un second « lu », que le serveur
  // refuserait (409) — un toast d'erreur juste après le toast de réussite.
  const actionEnCours = useRef(false);

  const marquerLu = async (avis) => {
    if (actionEnCours.current) return;
    actionEnCours.current = true;
    setEnCours(avis.id);
    try {
      const reponse = await avisEnseignants.marquerLu(avis.id);
      montrerToast(reponse?.message || 'Avis marqué lu.');
      await charger();
    } catch (e) {
      await refus(e);
    } finally {
      actionEnCours.current = false;
      setEnCours(null);
    }
  };

  const incidentCree = async (reponse) => {
    setIncidentPour(null);
    montrerToast(reponse?.message || "Incident créé : il figure désormais parmi les événements de la vie scolaire.");
    // Les compteurs et la liste des événements de la page se rafraîchissent.
    triggerVieScolaireUpdate();
    await charger();
  };

  const incidentRefuse = async (e) => {
    setIncidentPour(null);
    await refus(e);
  };

  const optionsClasses = useMemo(() => [
    { valeur: TOUTES, libelle: 'Toutes les classes' },
    ...(Array.isArray(classes) ? classes : [])
      .filter((c) => c?.id !== undefined && c?.id !== null)
      .map((c) => ({ valeur: String(c.id), libelle: c.nom || `Classe ${c.id}` }))
  ], [classes]);

  const compteurs = donnees?.compteurs || {};
  const total = Number(donnees?.total || 0);
  const parPage = Number(donnees?.par_page || 50);
  const nombrePages = Math.max(1, Math.ceil(total / parPage));
  const avis = Array.isArray(donnees?.avis) ? donnees.avis : [];
  const peutTraiter = Boolean(donnees?.droits?.traiter);
  const filtresPoses = tonalite !== TOUTES || classeId !== TOUTES || recherche.trim() !== '';
  const outilDecisionnel = estActive('module.evaluations.outil_decisionnel');
  const occupe = Boolean(enCours);

  const libelleStatut = (s) => {
    if (s.cle === 'tous') return s.libelle;
    const n = compteurs[s.cle];
    return n === undefined || n === null ? s.libelle : `${s.libelle} (${n})`;
  };

  const [titreVide, texteVide] = VIDES[statut] || VIDES.tous;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Statut des avis">
          {STATUTS.map((s) => {
            const actif = statut === s.cle;
            return (
              <button
                key={s.cle}
                type="button"
                aria-pressed={actif}
                onClick={() => filtrer(setStatut)(s.cle)}
                className={classesFiltre(actif)}
              >
                {libelleStatut(s)}
              </button>
            );
          })}
        </div>
        <div className="grid gap-2 sm:grid-cols-3 xl:flex xl:items-center">
          {/* Libellés lus par le lecteur d'écran seulement : à l'écran, la
              valeur choisie (« Toutes les tonalités ») dit déjà de quoi il s'agit. */}
          <div className="relative xl:w-[180px]">
            <label htmlFor={idTonalite} className="sr-only">Tonalité</label>
            <ChampSelect id={idTonalite} valeur={tonalite} onChange={filtrer(setTonalite)} options={TONALITES} />
          </div>
          <div className="relative xl:w-[180px]">
            <label htmlFor={idClasse} className="sr-only">Classe</label>
            <ChampSelect id={idClasse} valeur={classeId} onChange={filtrer(setClasseId)} options={optionsClasses} />
          </div>
          <label className="relative block xl:w-[230px]">
            <span className="sr-only">Rechercher un élève ou un enseignant</span>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={recherche}
              onChange={(e) => filtrer(setRecherche)(e.target.value)}
              placeholder="Élève ou enseignant"
              className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
            />
          </label>
        </div>
      </div>

      <div className="px-4 pb-2">
        {donnees && !peutTraiter && (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Vous pouvez lire les avis, mais pas les traiter : marquer un avis lu ou en faire un incident demande, dans
            le module Élèves, la permission « Vie scolaire : marquer lu un avis d'enseignant, en faire un incident ».
          </p>
        )}

        {/* Un échec qui laisse la zone vide s'affiche dans la zone, pas en toast :
            un toast disparaîtrait en laissant un vide inexpliqué. */}
        {erreur && (
          <div role="alert" className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">
              {donnees ? `La liste n'a pas pu être rafraîchie : ${erreur}` : erreur}
            </span>
            <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
          </div>
        )}

        {!donnees && !erreur && (
          <div role="status" className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-[#246990]" />
            Chargement des avis…
          </div>
        )}

        {donnees && avis.length === 0 && (
          <div className="py-4">
            {filtresPoses ? (
              <EtatVide
                compact
                icone={Inbox}
                titre="Aucun avis ne correspond à ces filtres"
                description="Élargissez la recherche, ou revenez à toutes les classes et toutes les tonalités."
                action={<Button size="sm" variant="outline" onClick={effacerFiltres}>Effacer les filtres</Button>}
              />
            ) : (
              <EtatVide compact icone={Inbox} titre={titreVide} description={texteVide} />
            )}
          </div>
        )}

        {avis.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {avis.map((a) => (
              <LigneAvis
                key={a.id}
                avis={a}
                actions={peutTraiter && a.statut !== 'incident' ? (
                  <>
                    {a.statut === 'nouveau' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-[150px] gap-2 font-semibold"
                        disabled={occupe}
                        // Le libellé visible se répète à chaque ligne : le lecteur
                        // d'écran doit savoir QUEL avis il marque.
                        aria-label={`Marquer lu ${intituleAvis(a)}`}
                        onClick={() => marquerLu(a)}
                      >
                        {enCours === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Marquer lu
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-[150px] gap-2 font-semibold"
                      disabled={occupe}
                      aria-label={`Créer un incident à partir de ${intituleAvis(a)}`}
                      onClick={() => setIncidentPour(a)}
                    >
                      <ShieldAlert className="h-4 w-4" />
                      Créer un incident
                    </Button>
                  </>
                ) : null}
              />
            ))}
          </ul>
        )}

        {donnees && total > 0 && (
          <PaginationAvis
            page={Number(donnees.page || page)}
            nombrePages={nombrePages}
            total={total}
            parPage={parPage}
            onChangerPage={setPage}
            desactive={chargement}
          />
        )}
      </div>

      <div className="px-4 pb-4 pt-1">
        <p className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-3 py-2.5 text-xs leading-relaxed text-gray-700">
          Un avis d'enseignant n'est jamais montré à la famille
          {outilDecisionnel ? " et n'entre pas dans le score de l'outil décisionnel" : ''}. Pour qu'un fait remonte aux
          parents, la vie scolaire crée elle-même un incident, qu'elle rédige.
        </p>
      </div>

      <DialogIncidentAvis
        avis={incidentPour}
        onFermer={() => setIncidentPour(null)}
        onCree={incidentCree}
        onRefus={incidentRefuse}
      />

      {toast && (
        <ToastPortal>
          {shouldUseLongToast(toast.message, toast.type, toast) ? (
            <LongToast show message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          ) : (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </ToastPortal>
      )}
    </div>
  );
};

export default AvisEnseignantsOnglet;
